// ============================================================
// tree - the library's folders: paths, ancestry, names. Pure.
//
// A folder is one row with a parent, so moving or renaming one is one write
// and nothing below it changes; everything here is derived from the rows.
// ============================================================
import { ROOT, type AssetRow, type FolderRow } from './types';

const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);

/** The folders from the top down to `folderId` (empty for My assets). A cycle stops rather than spins. */
export function pathOf(folders: readonly FolderRow[], folderId: string): FolderRow[] {
  const byId = new Map(folders.map((f) => [f.id, f]));
  const out: FolderRow[] = [];
  for (let f = byId.get(folderId); f && !out.includes(f); f = byId.get(f.parentId)) out.unshift(f);
  return out;
}

/** Is `folderId` the folder `ancestorId`, or inside it? Everything is inside My assets. */
export function under(folders: readonly FolderRow[], folderId: string, ancestorId: string): boolean {
  return ancestorId === ROOT || folderId === ancestorId || pathOf(folders, folderId).some((f) => f.id === ancestorId);
}

/** A folder's own folders, by name. */
export function childrenOf(folders: readonly FolderRow[], parentId: string): FolderRow[] {
  return folders.filter((f) => f.parentId === parentId).sort(byName);
}

/** How many live assets a folder holds, its subfolders included. */
export function countIn(folders: readonly FolderRow[], assets: readonly AssetRow[], folderId: string): number {
  return assets.filter((a) => !a.deletedAt && under(folders, a.folderId, folderId)).length;
}

/** May these folders go into `targetId`? Never into themselves or anything inside them. */
export function canMoveInto(folders: readonly FolderRow[], movingIds: readonly string[], targetId: string): boolean {
  return !movingIds.some((id) => under(folders, targetId, id));
}

/** `wanted`, or the first of "wanted 2", "wanted 3"… that nothing in `taken` is called. */
export function uniqueName(taken: readonly string[], wanted: string): string {
  const names = new Set(taken);
  if (!names.has(wanted)) return wanted;
  let n = 2;
  while (names.has(`${wanted} ${n}`)) n++;
  return `${wanted} ${n}`;
}

/** A duplicate's name: "Docks (copy)", then "Docks (copy 2)"… */
export function copyName(taken: readonly string[], name: string): string {
  const first = `${name} (copy)`;
  const names = new Set(taken);
  if (!names.has(first)) return first;
  let n = 2;
  while (names.has(`${name} (copy ${n})`)) n++;
  return `${name} (copy ${n})`;
}
